import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { track } from '@/services/analytics';

const SidebarContext = createContext(null);
const STORAGE_KEY = 'finlist.sidebarPinned';

export function SidebarProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedValue = window.localStorage.getItem(STORAGE_KEY);
      if (storedValue === 'true') {
        setIsPinned(true);
        setIsOpen(true);
      }
    } catch (error) {
      console.warn('Unable to read sidebar state from storage', error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, isPinned ? 'true' : 'false');
    } catch (error) {
      console.warn('Unable to persist sidebar state', error);
    }
  }, [isPinned]);

  const openSidebar = useCallback(() => {
    setIsOpen(true);
    track('sidebar_opened').catch(() => {});
  }, []);

  const closeSidebar = useCallback(() => setIsOpen(false), []);

  const toggleSidebar = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      track('sidebar_toggled', { open: next }).catch(() => {});
      return next;
    });
  }, []);

  const togglePin = useCallback(() => {
    setIsPinned(prev => {
      const next = !prev;
      if (next) {
        setIsOpen(true);
      }
      track('sidebar_pin_toggled', { pinned: next }).catch(() => {});
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    isOpen: isPinned ? true : isOpen,
    isPinned,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    togglePin
  }), [isOpen, isPinned, openSidebar, closeSidebar, toggleSidebar, togglePin]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}
